import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
  mergeStyleSets,
} from "@fluentui/react";
import { CaretLeft, Robot, Train as TrainIcon, Calendar, Info } from "@phosphor-icons/react";
import IconBadge from "../components/common/IconBadge";
import { getPhosphorIcon } from "../utils/iconMap";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { boroondaraPalette } from "../theme/boroondaraTheme";
import { accessService } from "../services/accessService";
import { useNavigate } from "react-router-dom";

// ── Types ─────────────────────────────────────────────
interface Kpis {
  total_entries: number;
  busiest_station: string;
  busiest_entries: number;
  avg_weekday: number;
  avg_weekend: number;
  stations_count: number;
  financial_year: string;
}

// ── Constants ─────────────────────────────────────────
const ACCENT = "#0EA5E9";
const COLORS = ["#0EA5E9","#38BDF8","#7DD3FC","#0284C7","#0369A1","#075985","#0C4A6E","#06B6D4","#22D3EE","#67E8F9","#14B8A6","#2DD4BF","#5EEAD4"];
const TOD_COLORS: Record<string, string> = {
  "Pre-AM Peak": "#7DD3FC",
  "AM Peak": "#0EA5E9",
  "Interpeak": "#06B6D4",
  "PM Peak": "#0284C7",
  "PM Late": "#0C4A6E",
};

const FY_OPTIONS: IDropdownOption[] = [
  { key: "2024-25", text: "2024-25" },
  { key: "2023-24", text: "2023-24" },
  { key: "2022-23", text: "2022-23" },
  { key: "2021-22", text: "2021-22" },
  { key: "2020-21", text: "2020-21" },
  { key: "2019-20", text: "2019-20" },
  { key: "2018-19", text: "2018-19" },
];

const STATION_LIST = [
  "Glenferrie", "Camberwell", "Auburn", "Hawthorn", "Gardiner",
  "Canterbury", "Glen Iris", "Kooyong", "East Camberwell",
  "Riversdale", "Hartwell", "Alamein", "Willison",
];

const STATION_OPTIONS: IDropdownOption[] = [
  { key: "all", text: "All Boroondara" },
  ...STATION_LIST.map(s => ({ key: s, text: s })),
];

const tooltipStyle = { backgroundColor: "#fff", border: "1px solid #E1E1E1", borderRadius: 4, color: "#232323", fontSize: 12 };

// ── Helpers ───────────────────────────────────────────
function fmtNum(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : n.toLocaleString("en-AU");
}
function fmtAxis(value: number): string {
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
  if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(0) + "K";
  return String(value);
}
function truncLabel(s: string, max = 14): string {
  return s.length > max ? s.slice(0, max) + "..." : s;
}

// ── Styles ───────────────────────────────────────────
const p = boroondaraPalette;
const styles = mergeStyleSets({
  page: { padding: 32, minHeight: "calc(100vh - 52px)", background: "#F5F5F5", "@media (max-width: 768px)": { padding: 16 } } as any,
  container: { maxWidth: 1280, margin: "0 auto" },
  headerRow: { marginBottom: 24 },
  title: { color: p.text, fontWeight: 800 as any, fontSize: 28, letterSpacing: "-0.02em" },
  subtitle: { color: p.text2, marginTop: 6, maxWidth: 780, lineHeight: "1.6", fontSize: 14 },
  chipRow: { display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" } as any,
  chip: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "#fff", border: "1px solid #E1E1E1", borderRadius: 16,
    padding: "4px 12px", fontSize: 12, color: p.text2,
  },
  filterRow: {
    display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap", alignItems: "flex-end",
    background: "#fff", borderRadius: 8, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  } as any,
  filterLabel: { fontSize: 12, fontWeight: 600, color: p.text2, marginBottom: 4 },
  kpiGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 16, marginBottom: 28,
    "@media (max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
  } as any,
  kpiCard: {
    background: "#fff", borderRadius: 10, padding: "20px 18px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: 4,
  } as any,
  kpiLabel: { fontSize: 12, color: p.text2, fontWeight: 600 },
  kpiValue: { fontSize: 24, fontWeight: 800 as any, color: p.text },
  kpiSub: { fontSize: 11, color: p.text3 },
  chartsRow: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28,
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  } as any,
  chartCard: {
    background: "#fff", borderRadius: 10, padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  chartTitle: { fontSize: 14, fontWeight: 700, color: p.text, marginBottom: 12 },
  tableWrap: {
    background: "#fff", borderRadius: 10, padding: 20, marginBottom: 28,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflowX: "auto",
  } as any,
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 } as any,
  th: { textAlign: "left" as any, padding: "8px 10px", borderBottom: "2px solid #E1E1E1", color: p.text2, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" as any },
  td: { padding: "7px 10px", borderBottom: "1px solid #F0F0F0", color: p.text, whiteSpace: "nowrap" as any },
  agentCta: {
    background: `linear-gradient(135deg, ${ACCENT}, #38BDF8)`, borderRadius: 12, padding: "24px 28px",
    display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer",
    transition: "transform 0.15s ease", marginBottom: 20,
    selectors: { ":hover": { transform: "translateY(-2px)" } },
  } as any,
  agentCtaText: { color: "#fff" },
  agentCtaTitle: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
  agentCtaSub: { fontSize: 13, opacity: 0.85 },
  agentCtaIcon: { fontSize: 32, color: "#fff", opacity: 0.9 },
  attribution: { fontSize: 11, color: p.text3, textAlign: "center", padding: "12px 0" } as any,
  error: { marginBottom: 20 },
});

// ══════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════
export default function PublicTransport() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [comparisonData, setComparisonData] = useState<any[]>([]);
  const [todData, setTodData] = useState<any[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);
  const [stationData, setStationData] = useState<any[]>([]);

  const [fy, setFy] = useState("2024-25");
  const [station, setStation] = useState("all");

  // ── Fetch KPIs ──
  const fetchKpis = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await accessService.get(`/public-transport/kpis?fy=${fy}`);
      setKpis(res.data);
    } catch (err: any) {
      setError(err.message || "Failed to load public transport data");
    } finally {
      setLoading(false);
    }
  }, [fy]);

  useEffect(() => { fetchKpis(); }, [fetchKpis]);

  // ── Fetch comparison data ──
  const fetchComparison = useCallback(async () => {
    try {
      const res = await accessService.get(`/public-transport/comparison?fy=${fy}&metric=pax_annual`);
      setComparisonData(res.data);
    } catch (err: any) {
      console.error("Comparison error:", err);
    }
  }, [fy]);

  useEffect(() => { fetchComparison(); }, [fetchComparison]);

  // ── Fetch time-of-day data ──
  const fetchTod = useCallback(async () => {
    try {
      const stParam = station !== "all" ? `&station=${encodeURIComponent(station)}` : "";
      const res = await accessService.get(`/public-transport/time-of-day?fy=${fy}${stParam}`);
      setTodData(res.data);
    } catch (err: any) {
      console.error("Time-of-day error:", err);
    }
  }, [fy, station]);

  useEffect(() => { fetchTod(); }, [fetchTod]);

  // ── Fetch trend data ──
  const fetchTrend = useCallback(async () => {
    try {
      const stParam = station !== "all" ? `station=${encodeURIComponent(station)}` : "station=all";
      const res = await accessService.get(`/public-transport/trend?${stParam}&metric=pax_annual`);
      setTrendData(res.data);
    } catch (err: any) {
      console.error("Trend error:", err);
    }
  }, [station]);

  useEffect(() => { fetchTrend(); }, [fetchTrend]);

  // ── Fetch station table data ──
  const fetchStations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set("fy", fy);
      if (station !== "all") params.set("station", station);
      const res = await accessService.get(`/public-transport/stations?${params}`);
      setStationData(res.data);
    } catch (err: any) {
      console.error("Stations error:", err);
    }
  }, [fy, station]);

  useEffect(() => { fetchStations(); }, [fetchStations]);

  // ── Grouped bar data: weekday vs sat vs sun ──
  const groupedBarData = useMemo(() => {
    if (stationData.length === 0) return [];
    return stationData
      .map(row => ({
        stop_name: row.stop_name,
        Weekday: Number(row.pax_weekday) || 0,
        Saturday: Number(row.pax_saturday) || 0,
        Sunday: Number(row.pax_sunday) || 0,
      }))
      .sort((a, b) => b.Weekday - a.Weekday);
  }, [stationData]);

  // ── KPI cards ──
  const kpiCards = useMemo(() => {
    if (!kpis) return [];
    return [
      { icon: "Train", color: ACCENT, label: "Total Entries", value: fmtNum(kpis.total_entries), sub: `FY ${kpis.financial_year}` },
      { icon: "Trophy2", color: "#0284C7", label: "Busiest Station", value: kpis.busiest_station || "-", sub: fmtNum(kpis.busiest_entries) + " entries" },
      { icon: "DateTime", color: "#06B6D4", label: "Avg Weekday", value: fmtNum(kpis.avg_weekday), sub: "daily entries per station" },
      { icon: "Calendar", color: "#14B8A6", label: "Avg Weekend", value: fmtNum(kpis.avg_weekend), sub: "avg Sat + Sun per station" },
    ];
  }, [kpis]);

  // ══════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════
  if (loading && !kpis) {
    return (
      <div className={styles.page}>
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 120 }}>
          <Spinner size={SpinnerSize.large} label="Loading public transport data..." />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>

        {/* ── Breadcrumb ── */}
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => nav('/dashboards')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, color: ACCENT, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <CaretLeft size={10} weight="bold" /> Dashboards
          </button>
        </div>

        {/* ── Header ── */}
        <div className={styles.headerRow}>
          <div className={styles.title}>Public Transport</div>
          <div className={styles.subtitle}>
            Train station patronage across Boroondara — real data from Transport for Victoria.
            Covers {STATION_LIST.length} stations across the Belgrave/Lilydale, Glen Waverley, and Alamein lines.
          </div>
          <div className={styles.chipRow}>
            <span className={styles.chip}><TrainIcon size={12} weight="regular" /> 13 Stations</span>
            <span className={styles.chip}><Calendar size={12} weight="regular" /> 7 Financial Years</span>
            <span className={styles.chip}><Info size={12} weight="regular" /> PTV Open Data</span>
          </div>
        </div>

        {error && (
          <MessageBar messageBarType={MessageBarType.error} className={styles.error}>{error}</MessageBar>
        )}

        {/* ── Filters ── */}
        <div className={styles.filterRow}>
          <div>
            <div className={styles.filterLabel}>Financial Year</div>
            <Dropdown
              selectedKey={fy}
              options={FY_OPTIONS}
              onChange={(_, opt) => opt && setFy(String(opt.key))}
              styles={{ root: { width: 160 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>Station</div>
            <Dropdown
              selectedKey={station}
              options={STATION_OPTIONS}
              onChange={(_, opt) => opt && setStation(String(opt.key))}
              styles={{ root: { width: 220 } }}
            />
          </div>
        </div>

        {/* ── KPI Cards ── */}
        <div className={styles.kpiGrid}>
          {kpiCards.map((kpi, i) => (
            <div key={i} className={styles.kpiCard}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <IconBadge icon={getPhosphorIcon(kpi.icon)} size="sm" color={kpi.color} weight="fill" />
                <span className={styles.kpiLabel}>{kpi.label}</span>
              </div>
              <div className={styles.kpiValue} style={{ color: kpi.color }}>{kpi.value}</div>
              <div className={styles.kpiSub}>{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Charts Row 1 ── */}
        <div className={styles.chartsRow}>
          {/* Bar chart: patronage by station */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Patronage by Station ({fy})</div>
            {comparisonData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={comparisonData} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="stop_name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0}
                    tickFormatter={(v: string) => truncLabel(v)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Bar dataKey="value" name="Annual Entries" radius={[4, 4, 0, 0]}>
                    {comparisonData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No patronage data available
              </div>
            )}
          </div>

          {/* Pie chart: time-of-day distribution */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>
              Time-of-Day Distribution ({fy}) {station !== "all" ? `- ${station}` : ""}
            </div>
            {todData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie data={todData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    outerRadius={120} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={true} fontSize={11}>
                    {todData.map((entry, i) => (
                      <Cell key={i} fill={TOD_COLORS[entry.name] || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No time-of-day data available
              </div>
            )}
          </div>
        </div>

        {/* ── Charts Row 2 ── */}
        <div className={styles.chartsRow}>
          {/* Line chart: annual trend */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>
              Annual Trend {station !== "all" ? `- ${station}` : "- All Boroondara"}
            </div>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="financial_year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Line type="monotone" dataKey="value" name="Annual Entries" stroke={ACCENT} strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No trend data available
              </div>
            )}
          </div>

          {/* Grouped bar: weekday vs sat vs sun */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Weekday vs Weekend Entries ({fy})</div>
            {groupedBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={groupedBarData} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="stop_name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0}
                    tickFormatter={(v: string) => truncLabel(v)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Weekday" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Saturday" fill="#06B6D4" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Sunday" fill="#7DD3FC" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No station data available
              </div>
            )}
          </div>
        </div>

        {/* ── Data Table ── */}
        <div className={styles.tableWrap}>
          <div className={styles.chartTitle}>
            Station Patronage — FY {fy}
            {station !== "all" && ` — ${station}`}
          </div>
          {stationData.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Station</th>
                  <th className={styles.th}>Annual Entries</th>
                  <th className={styles.th}>Weekday</th>
                  <th className={styles.th}>Sat</th>
                  <th className={styles.th}>Sun</th>
                  <th className={styles.th}>AM Peak</th>
                  <th className={styles.th}>Interpeak</th>
                  <th className={styles.th}>PM Peak</th>
                  <th className={styles.th}>FY</th>
                </tr>
              </thead>
              <tbody>
                {stationData.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#FAFAFA" : "#fff" }}>
                    <td className={styles.td} style={{ fontWeight: 600 }}>{row.stop_name}</td>
                    <td className={styles.td}>{fmtNum(row.pax_annual)}</td>
                    <td className={styles.td}>{fmtNum(row.pax_weekday)}</td>
                    <td className={styles.td}>{fmtNum(row.pax_saturday)}</td>
                    <td className={styles.td}>{fmtNum(row.pax_sunday)}</td>
                    <td className={styles.td}>{fmtNum(row.pax_am_peak)}</td>
                    <td className={styles.td}>{fmtNum(row.pax_interpeak)}</td>
                    <td className={styles.td}>{fmtNum(row.pax_pm_peak)}</td>
                    <td className={styles.td}>{row.financial_year}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 40, textAlign: "center", color: p.text3 }}>
              No station data available for the selected filters.
            </div>
          )}
        </div>

        {/* ── Agent CTA ── */}
        <div className={styles.agentCta} onClick={() => nav("/chat")} role="button" tabIndex={0}>
          <div className={styles.agentCtaText}>
            <div className={styles.agentCtaTitle}>Ask the AI assistant about public transport patterns</div>
            <div className={styles.agentCtaSub}>
              Explore station patronage, peak-hour trends, COVID recovery patterns, and compare ridership across Boroondara's train network.
            </div>
          </div>
          <Robot size={32} weight="duotone" color="#fff" style={{ opacity: 0.9 }} />
        </div>

        {/* ── Data Attribution ── */}
        <div className={styles.attribution}>
          Source: Transport for Victoria — Annual Metropolitan Train Station Patronage (CC-BY 4.0)
        </div>
      </div>
    </div>
  );
}
